import io from 'socket.io-client';

/**
 * Safely parse JSON with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {*} defaultValue - Default value to return if parsing fails
 * @returns {*} The parsed object or the default value
 */
const safeJsonParse = (jsonString, defaultValue = null) => {
  if (!jsonString) return defaultValue;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Error parsing JSON:', error);
    return defaultValue;
  }
};

/**
 * Safely access localStorage with error handling
 */
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
      return false;
    }
  }
};

// Create a singleton socket instance and online users cache
let socket = null;
let onlineUsers = {
  count: 1,
  users: {}
};

// For multiplayer game invitations and active games
let gameInvitations = {};
let activeMultiplayerGames = {};

// Mock socket events for local use
const mockSocketEvents = {};

// Constants for user activity
const USER_ACTIVITY_TIMEOUT = 60 * 1000; // 60 seconds
const REFRESH_INTERVAL = 30 * 1000; // 30 seconds

/**
 * Initialize and get the socket connection
 * @returns {Object} A mock socket instance for local use
 */
export const getSocket = () => {
  if (!socket) {
    // Create mock socket connection
    socket = {
      id: `local-${Date.now()}`,
      emit: (event, data) => {
        console.log(`Mock socket emitting ${event}:`, data);
        
        // Handle specific events locally
        if (event === 'join_game') {
          const userData = data;
          
          // Check if this is a multiplayer game join
          if (userData.gameId && userData.gameId.startsWith('game_')) {
            // This is a multiplayer game join
            const gameId = userData.gameId;
            
            // Create the game if it doesn't exist
            if (!activeMultiplayerGames[gameId]) {
              activeMultiplayerGames[gameId] = {
                gameId,
                word: getRandomMultiplayerWord(),
                players: [],
                currentTurn: userData.userId,
                playerStates: {},
                createdAt: new Date().toISOString()
              };
            }
            
            // Add the player to the game if not already there
            if (!activeMultiplayerGames[gameId].players.includes(userData.userId)) {
              activeMultiplayerGames[gameId].players.push(userData.userId);
              
              // Initialize player state
              activeMultiplayerGames[gameId].playerStates[userData.userId] = {
                guessedLetters: [],
                wrongGuesses: 0,
              };
              
              // Notify other players
              if (mockSocketEvents['player_joined']) {
                mockSocketEvents['player_joined'].forEach(callback => {
                  callback({
                    gameId,
                    player: userData.userId,
                    username: userData.username || userData.twitterUsername,
                    timestamp: new Date().toISOString()
                  });
                });
              }
              
              // Send game update to all players
              if (mockSocketEvents['multiplayer_game_update']) {
                mockSocketEvents['multiplayer_game_update'].forEach(callback => {
                  callback(activeMultiplayerGames[gameId]);
                });
              }
            }
          } else {
            // Regular game join (single player)
            // Add user to online users
            const username = userData.username || userData.twitterUsername;
            onlineUsers.users[username] = {
              id: socket.id,
              username: username,
              country: userData.country,
              lastActive: new Date().toISOString()
            };
            
            // Save the current user to localStorage for persistence
            safeLocalStorage.setItem('currentOnlineUser', JSON.stringify({
              username: username,
              country: userData.country,
              lastActive: new Date().toISOString()
            }));
            
            onlineUsers.count = Object.keys(onlineUsers.users).length;
            
            // Trigger online users update
            if (mockSocketEvents['online_users_update']) {
              mockSocketEvents['online_users_update'].forEach(callback => {
                callback(onlineUsers);
              });
            }
          }
        } else if (event === 'game_update') {
          if (mockSocketEvents['game_update']) {
            mockSocketEvents['game_update'].forEach(callback => {
              callback(data);
            });
          }
        } else if (event === 'multiplayer_game_update') {
          if (mockSocketEvents['multiplayer_game_update']) {
            mockSocketEvents['multiplayer_game_update'].forEach(callback => {
              callback(data);
            });
          }
        } else if (event === 'game_invitation') {
          // Store the invitation
          gameInvitations[data.invitationId] = data;
          
          if (mockSocketEvents['game_invitation']) {
            mockSocketEvents['game_invitation'].forEach(callback => {
              callback(data);
            });
          }
        } else if (event === 'invitation_response') {
          if (mockSocketEvents['invitation_response']) {
            mockSocketEvents['invitation_response'].forEach(callback => {
              callback(data);
            });
          }
          
          // If accepted, start game and notify both players
          if (data.accepted) {
            // Create game if it doesn't exist
            if (!activeMultiplayerGames[data.gameId]) {
              activeMultiplayerGames[data.gameId] = {
                gameId: data.gameId,
                word: getRandomMultiplayerWord(),
                players: [data.fromUserId, data.toUserId],
                currentTurn: data.fromUserId, // Inviter starts
                playerStates: {
                  [data.fromUserId]: { guessedLetters: [], wrongGuesses: 0 },
                  [data.toUserId]: { guessedLetters: [], wrongGuesses: 0 }
                },
                createdAt: new Date().toISOString()
              };
            }
            
            // Notify both players to start the game
            if (mockSocketEvents['game_start']) {
              mockSocketEvents['game_start'].forEach(callback => {
                callback({
                  gameId: data.gameId,
                  fromUserId: data.fromUserId,
                  toUserId: data.toUserId,
                  fromUsername: data.fromUsername,
                  toUsername: data.toUsername,
                  startedAt: new Date().toISOString()
                });
              });
            }
            
            // Send initial game state
            if (mockSocketEvents['multiplayer_game_update']) {
              mockSocketEvents['multiplayer_game_update'].forEach(callback => {
                callback(activeMultiplayerGames[data.gameId]);
              });
            }
          }
        }
      },
      // Add the missing on and off methods
      on: (event, callback) => {
        if (!mockSocketEvents[event]) {
          mockSocketEvents[event] = [];
        }
        mockSocketEvents[event].push(callback);
      },
      off: (event, callback) => {
        if (mockSocketEvents[event]) {
          mockSocketEvents[event] = mockSocketEvents[event].filter(cb => cb !== callback);
        }
      },
      disconnect: () => {
        console.log('Mock socket disconnected');
      }
    };
    
    // Try to restore the current user from localStorage on socket initialization
    restoreCurrentUser();
    
    // Start the interval to remove inactive users
    startUserActivityCheck();
  }
  return socket;
};

/**
 * Check for inactive users and remove them
 */
const startUserActivityCheck = () => {
  // Run initial check for stale users
  checkInactiveUsers();
  
  // Set interval to check for inactive users
  setInterval(() => {
    checkInactiveUsers();
  }, REFRESH_INTERVAL);
};

/**
 * Check and remove inactive users
 */
const checkInactiveUsers = () => {
  const now = new Date().getTime();
  let userRemoved = false;
  
  Object.entries(onlineUsers.users).forEach(([username, userData]) => {
    const lastActiveTime = new Date(userData.lastActive).getTime();
    // If user hasn't been active for more than the timeout period
    if (now - lastActiveTime > USER_ACTIVITY_TIMEOUT) {
      // Skip removal if it's the current user (keep them online)
      const currentUserData = safeJsonParse(safeLocalStorage.getItem('currentOnlineUser'));
      if (currentUserData && currentUserData.username === username) {
        // Just update their lastActive time
        onlineUsers.users[username].lastActive = new Date().toISOString();
        safeLocalStorage.setItem('currentOnlineUser', JSON.stringify({
          ...currentUserData,
          lastActive: new Date().toISOString()
        }));
      } else {
        // Remove inactive user
        delete onlineUsers.users[username];
        userRemoved = true;
      }
    }
  });
  
  // Update count if users were removed
  if (userRemoved) {
    onlineUsers.count = Object.keys(onlineUsers.users).length;
    // Notify subscribers
    triggerOnlineUsersUpdate();
  }
};

/**
 * Restore the current user from localStorage
 */
const restoreCurrentUser = () => {
  const currentUserData = safeJsonParse(safeLocalStorage.getItem('currentOnlineUser'));
  if (currentUserData && currentUserData.username) {
    // Add user back to online users list
    onlineUsers.users[currentUserData.username] = {
      id: socket.id,
      username: currentUserData.username,
      country: currentUserData.country, 
      lastActive: new Date().toISOString() // Update the last active time
    };
    onlineUsers.count = Object.keys(onlineUsers.users).length;
  }
};

/**
 * Trigger online users update event
 */
const triggerOnlineUsersUpdate = () => {
  if (mockSocketEvents['online_users_update']) {
    mockSocketEvents['online_users_update'].forEach(callback => {
      callback(onlineUsers);
    });
  }
};

// Add mock online users for testing
const addMockOnlineUsers = () => {
  const mockUsers = [
    { username: 'JohnDev92', country: 'United States' },
    { username: 'EmmaGamer', country: 'Canada' },
    { username: 'CodeMaster', country: 'United Kingdom' },
    { username: 'SydneyHacker', country: 'Australia' },
    { username: 'BerlinCoder', country: 'Germany' },
    { username: 'TokyoTech', country: 'Japan' },
    { username: 'ParisProgram', country: 'France' },
    { username: 'MadridDev', country: 'Spain' }
  ];
  
  mockUsers.forEach(user => {
    onlineUsers.users[user.username] = {
      id: `mock-${Date.now()}-${Math.random()}`,
      username: user.username,
      country: user.country,
      lastActive: new Date().toISOString()
    };
  });
  
  onlineUsers.count = Object.keys(onlineUsers.users).length;
};

// Call this function to add mock users
setTimeout(addMockOnlineUsers, 2000);

/**
 * Get a random word for multiplayer games
 * @returns {string} A random word
 */
const getRandomMultiplayerWord = () => {
  const words = [
    'JAVASCRIPT', 'REACT', 'COMPONENT', 'FUNCTION', 'VARIABLE',
    'MULTIPLAYER', 'SOCKET', 'SERVER', 'CLIENT', 'DATABASE',
    'ALGORITHM', 'INTERFACE', 'KEYBOARD', 'MONITOR', 'NETWORK',
    'PROTOCOL', 'ROUTER', 'SECURITY', 'TERMINAL', 'VIRTUAL',
    'WIRELESS', 'BROWSER', 'COOKIE', 'DOMAIN', 'ENCRYPTION',
    'FIREWALL', 'GATEWAY', 'HOSTING', 'INTERNET', 'JAVASCRIPT',
    'KERNEL', 'LOCALHOST', 'METADATA', 'NAMESPACE', 'OPERATOR',
    'PASSWORD', 'QUERY', 'RUNTIME', 'STYLESHEET', 'TEMPLATE',
    'USERNAME', 'VERSION', 'WEBSITE', 'XML', 'YOUTUBE',
    'HANGMAN', 'PUZZLE', 'CHALLENGE', 'VICTORY', 'DEFEAT'
  ];
  
  return words[Math.floor(Math.random() * words.length)];
};

/**
 * Join a game
 * @param {Object} userData - User data including username and game ID
 */
export const joinGame = (userData) => {
  const socket = getSocket();
  
  // Update user data with current timestamp
  userData.joinedAt = new Date().toISOString();
  
  // Save current user data for persistence
  if (userData.username || userData.twitterUsername) {
    const username = userData.username || userData.twitterUsername;
    safeLocalStorage.setItem('currentOnlineUser', JSON.stringify({
      username: username,
      country: userData.country,
      lastActive: new Date().toISOString()
    }));
    
    // Make sure user is in the online users list
    onlineUsers.users[username] = {
      id: socket.id,
      username: username,
      country: userData.country,
      lastActive: new Date().toISOString()
    };
    
    // Update count and trigger update
    onlineUsers.count = Object.keys(onlineUsers.users).length;
    triggerOnlineUsersUpdate();
  }
  
  socket.emit('join_game', userData);
};

/**
 * Send a game update
 * @param {Object} data - Game update data
 */
export const sendGameUpdate = (data) => {
  const socket = getSocket();
  socket.emit('game_update', data);
  
  // We don't need to update localStorage here as updateUserStats already does this
  // This prevents double-counting of games played
};

/**
 * Search for a player by username
 * @param {string} username - Username to search for
 * @returns {Object|null} User data if found, null otherwise
 */
export const searchPlayer = (username) => {
  if (!username || !onlineUsers.users) return null;
  
  // Exact match
  if (onlineUsers.users[username]) {
    return { ...onlineUsers.users[username], username };
  }
  
  // Case-insensitive search
  const searchLower = username.toLowerCase();
  const match = Object.entries(onlineUsers.users).find(([key, _]) => 
    key.toLowerCase().includes(searchLower)
  );
  
  if (match) {
    const [foundUsername, userData] = match;
    return { ...userData, username: foundUsername };
  }
  
  return null;
};

/**
 * Send a game invitation to another player
 * @param {Object} data - Invitation data including from, to, and gameId
 */
export const sendGameInvitation = (data) => {
  const socket = getSocket();
  socket.emit('game_invitation', data);
};

/**
 * Respond to a game invitation
 * @param {Object} data - Response data including gameId, response (accepted/rejected), fromUser, toUser
 */
export const respondToInvitation = (data) => {
  const socket = getSocket();
  socket.emit('invitation_response', data);
};

/**
 * Send a multiplayer game update
 * @param {Object} data - Game update data including gameId, player, guessedLetter
 */
export const sendMultiplayerGameUpdate = (data) => {
  const socket = getSocket();
  socket.emit('multiplayer_game_update', data);
};

/**
 * Listen for online users updates
 * @param {Function} callback - Callback function to handle online users data
 * @returns {Function} Function to remove the listener
 */
export const onOnlineUsersUpdate = (callback) => {
  const socket = getSocket();
  
  // Register the callback with the mock socket events
  if (!mockSocketEvents['online_users_update']) {
    mockSocketEvents['online_users_update'] = [];
  }
  
  // Only add the callback if it's not already in the array
  if (!mockSocketEvents['online_users_update'].includes(callback)) {
    mockSocketEvents['online_users_update'].push(callback);
  }
  
  // Immediately trigger with current data
  setTimeout(() => {
    // Make sure we're still active before calling
    if (mockSocketEvents['online_users_update'].includes(callback)) {
      callback(onlineUsers);
    }
  }, 100);
  
  // Update user activity timestamp when this is called
  // This helps track active users by their interactions
  const currentUserData = safeJsonParse(safeLocalStorage.getItem('currentOnlineUser'));
  if (currentUserData && currentUserData.username) {
    // Update the last active time in localStorage
    safeLocalStorage.setItem('currentOnlineUser', JSON.stringify({
      ...currentUserData,
      lastActive: new Date().toISOString()
    }));
    
    // Also update in the onlineUsers object
    if (onlineUsers.users[currentUserData.username]) {
      onlineUsers.users[currentUserData.username].lastActive = new Date().toISOString();
    }
  }
  
  // Return a function to remove the listener
  return () => {
    // Remove the callback from the mock socket events
    if (mockSocketEvents['online_users_update']) {
      const index = mockSocketEvents['online_users_update'].indexOf(callback);
      if (index !== -1) {
        mockSocketEvents['online_users_update'].splice(index, 1);
      }
    }
  };
};

/**
 * Listen for player joined events
 * @param {Function} callback - Callback function to handle player joined data
 * @returns {Function} Function to remove the listener
 */
export const onPlayerJoined = (callback) => {
  const socket = getSocket();
  socket.on('player_joined', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('player_joined', callback);
  };
};

/**
 * Listen for game update events
 * @param {Function} callback - Callback function to handle game update data
 * @returns {Function} Function to remove the listener
 */
export const onGameUpdate = (callback) => {
  const socket = getSocket();
  socket.on('game_update', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('game_update', callback);
  };
};

/**
 * Listen for game invitation events
 * @param {Function} callback - Callback function to handle game invitation data
 * @returns {Function} Function to remove the listener
 */
export const onGameInvitation = (callback) => {
  const socket = getSocket();
  socket.on('game_invitation', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('game_invitation', callback);
  };
};

/**
 * Listen for invitation response events
 * @param {Function} callback - Callback function to handle invitation response data
 * @returns {Function} Function to remove the listener
 */
export const onInvitationResponse = (callback) => {
  const socket = getSocket();
  socket.on('invitation_response', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('invitation_response', callback);
  };
};

/**
 * Listen for multiplayer game start events
 * @param {Function} callback - Callback function to handle game start data
 * @returns {Function} Function to remove the listener
 */
export const onGameStart = (callback) => {
  const socket = getSocket();
  socket.on('game_start', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('game_start', callback);
  };
};

/**
 * Listen for multiplayer game update events
 * @param {Function} callback - Callback function to handle multiplayer game update data
 * @returns {Function} Function to remove the listener
 */
export const onMultiplayerGameUpdate = (callback) => {
  const socket = getSocket();
  socket.on('multiplayer_game_update', callback);
  
  // Return a function to remove the listener
  return () => {
    socket.off('multiplayer_game_update', callback);
  };
};

/**
 * Disconnect the socket and handle cleanup
 */
export const disconnectSocket = () => {
  // We don't actually disconnect, since this is a mock socket
  // But we'll mark the user as inactive if they're logged out
  
  // Get current user data from localStorage
  const currentUserData = safeJsonParse(safeLocalStorage.getItem('currentOnlineUser'));
  
  if (currentUserData && currentUserData.username) {
    // Mark user as inactive rather than removing
    // This will allow them to be automatically removed after the timeout
    // if they don't return, but immediately recovered if they reload
    if (onlineUsers.users[currentUserData.username]) {
      // Set lastActive to a time that will trigger removal soon
      // but not immediately
      const oldTime = new Date();
      oldTime.setSeconds(oldTime.getSeconds() - 50); // 50 seconds ago
      
      onlineUsers.users[currentUserData.username].lastActive = oldTime.toISOString();
    }
  }
  
  // Don't actually disconnect the socket in our mock implementation
  if (socket) {
    console.log('Mock socket marked for potential disconnection');
  }
}; 